/**
 * BCPFormLayout - Shared layout structure for BCP forms
 */

import React, { useState, useRef, useEffect } from 'react';
import BCPPageHeader from '../BCPPageHeader';
import BCPStepIndicator from '../BCPStepIndicator';
import FloatingAIAssistant from '../FloatingAIAssistant';
import { useHeaderMenuItems } from '../../hooks/useHeaderMenuItems';

interface FieldContext {
  fieldId?: string;
  fieldLabel?: string;
  currentValue?: string;
  allFormData?: Record<string, unknown>;
}

interface BCPFormLayoutProps {
  children: React.ReactNode;
  currentStep?: number;
  showStepIndicator?: boolean;
  // ヘッダーアクション用のプロパティ
  showHeaderActions?: boolean;
  showHeaderMenu?: boolean;
  isEditMode?: boolean;
  onSave?: () => void | Promise<void>;
  onDownload?: () => void | Promise<void>;
  saveLoading?: boolean;
  downloadLoading?: boolean;
  saveDisabled?: boolean;
  downloadDisabled?: boolean;
  // AI相談用のプロパティ
  fieldContext?: FieldContext;
  onAIContentApply?: (content: string) => void;
  bcpType?: 'simple' | 'basic';
  // トップコンテンツ(全幅)
  topContent?: React.ReactNode;
}

const DESKTOP_BREAKPOINT = 1024;

const getInitialIsSmallScreen = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const width = Number(window.innerWidth ?? DESKTOP_BREAKPOINT);
  return width < DESKTOP_BREAKPOINT;
};

const BCPFormLayout: React.FC<BCPFormLayoutProps> = ({
  children,
  currentStep = 2,
  showStepIndicator = true,
  showHeaderActions = false,
  showHeaderMenu = false,
  isEditMode = false,
  onSave,
  onDownload,
  saveLoading = false,
  downloadLoading = false,
  saveDisabled = false,
  downloadDisabled = false,
  fieldContext,
  onAIContentApply,
  bcpType = 'simple',
  topContent,
}) => {
  const headerMenuItems = useHeaderMenuItems();
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(() => getInitialIsSmallScreen());
  const [assistantWidth, setAssistantWidth] = useState(601);
  const containerRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setIsSmallScreen(getInitialIsSmallScreen());

      // アシスタント幅の計算: 1862px (全幅) - 1237px (フォーム) - 24px (gap) = 601px
      const width = window.innerWidth;
      if (width >= 1440) {
        setAssistantWidth(601);
      } else if (width >= 1280) {
        setAssistantWidth(480);
      } else {
        setAssistantWidth(width);
      }
    };

    handleResize(); // 初期化
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // fieldContextが変更されたときにチャットパネルを自動的に開く
  useEffect(() => {
    if (fieldContext?.fieldId) {
      setIsAIAssistantOpen(true);
    }
  }, [fieldContext?.fieldId]);

  const assistantProps = {
    isOpen: isAIAssistantOpen,
    onToggle: () => setIsAIAssistantOpen((prev) => !prev),
    containerRef: containerRef as React.RefObject<HTMLElement>,
    contentRef: mainContentRef as React.RefObject<HTMLElement>,
    isSmallScreen,
    fieldContext,
    onApply: onAIContentApply,
    bcpType,
  };

  return (
    <div ref={containerRef} className="min-h-screen" style={{ backgroundColor: '#F0EDE5' }}>
      {/* data属性を追加して自動測定を可能にする */}
      <div data-bcp-header>
        <BCPPageHeader
          showActions={showHeaderActions}
          isEditMode={isEditMode}
          onSave={onSave}
          onDownload={onDownload}
          saveLoading={saveLoading}
          downloadLoading={downloadLoading}
          saveDisabled={saveDisabled}
          downloadDisabled={downloadDisabled}
          menuItems={showHeaderMenu ? headerMenuItems : []}
        />
      </div>

      {showStepIndicator && (
        <div data-bcp-step-indicator>
          <BCPStepIndicator currentStep={currentStep} />
        </div>
      )}

      {/* メインコンテンツ - Figmaのデザインに合わせて横並びレイアウト */}
      <main
        className="flex-1 min-h-screen transition-all duration-300"
        style={{
          backgroundColor: '#F0EDE5',
          padding: '32px 29px',
        }}
      >
        <div
          className="mx-auto"
          style={{
            maxWidth: '1862px',
          }}
        >
          {/* トップコンテンツ(全幅) */}
          {topContent && (
            <div className="mb-10">
              {topContent}
            </div>
          )}

          {/* フォームとAIアシスタントの横並びレイアウト */}
          <div className="flex gap-6">
            {/* 左側: フォームコンテンツ */}
            <div
              className="flex-1"
              ref={mainContentRef}
              data-bcp-main
              style={{
                maxWidth: '1237px',
              }}
            >
              {children}
            </div>

            {/* 右側: AIアシスタント */}
            <div
              className="flex-shrink-0"
              style={{
                width: isSmallScreen ? '100%' : `${assistantWidth}px`,
                maxWidth: '601px',
                position: 'sticky',
                top: '32px',
                alignSelf: 'flex-start',
              }}
            >
              <FloatingAIAssistant
                {...assistantProps}
                displayMode="inline"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BCPFormLayout;
